import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CheckSnackbarsService } from '@qaroni-app/check/services/check-snackbars.service';
import { Check, CheckReport } from '@qaroni-app/check/types/check';
import { CheckHistory } from '@qaroni-app/check/types/check-history';
import { CheckHistoryApiResponse } from '@qaroni-app/check/types/check-history-response';
import { CheckRegisterApiResponse } from '@qaroni-app/check/types/check-register-response';
import { CheckReportApiResponse } from '@qaroni-app/check/types/check-report-response';
import { Journey } from '@qaroni-app/check/types/journey';
import { Month } from '@qaroni-app/check/types/period';
import { CommonsHttpService } from '@qaroni-core/services/commons/commons-http/commons-http.service';
import { Observable, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ScheduleCheckHttpService } from './schedule-check-http.service';

@Injectable({
  providedIn: 'root',
})
export class ScheduleCheckService {
  private http = inject(ScheduleCheckHttpService);
  private commonsHttp = inject(CommonsHttpService);
  private snackbars = inject(CheckSnackbarsService);

  protected readonly checkSubject = new Subject<Check>();
  protected readonly journeySubject = new Subject<Journey>();
  protected readonly messageSubject = new Subject<string>();
  protected readonly historySubject = new Subject<CheckHistory[]>();
  protected readonly checksReportSubject = new Subject<CheckReport | null>();

  // ==========================================================================================
  // Register Check From Company
  // ==========================================================================================

  public getCheck$(): Observable<Check> {
    return this.checkSubject.asObservable();
  }

  public getJourney$(): Observable<Journey> {
    return this.journeySubject.asObservable();
  }

  public registerCheckFromCompany(
    companyID: number,
    employeeID: number,
    check: Check
  ): void {
    this.http
      .registerCheckFromCompany$(companyID, employeeID, check)
      .subscribe({
        next: this.nextRegisterCheckFromCompany,
        error: this.errorRegisterCheckFromCompany,
      });
  }

  private nextRegisterCheckFromCompany = (
    data: CheckRegisterApiResponse
  ): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus201(data)) {
      const check: Check = data.body.result[0] as Check;
      const journey: Journey = data.body.included as unknown as Journey;
      this.checkSubject.next(check);
      this.journeySubject.next(journey);
      this.snackbars.successRegisterCheck();
    } else {
      this.checkSubject.next(null);
      this.journeySubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorRegisterCheckFromCompany = (error: HttpErrorResponse): void => {
    this.checkSubject.next(null);
    this.journeySubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0004')) {
      this.snackbars.failureWrongHours();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0008')) {
      this.snackbars.failureEmployeeInvalidCredentials();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0020')) {
      this.snackbars.failureEmployeeNotWithInWorkplaceAccessRadius();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0028')) {
      this.snackbars.failureCheckDateCollidesWithAnExistingOne();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0030')) {
      this.snackbars.failureCheckCalendarIsClosed();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0055')) {
      this.snackbars.failureEmployeeOnLeave();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Update Check
  // ==========================================================================================

  public updateCheckFromCompany(
    companyID: number,
    employeeID: number,
    checkID: number,
    check: Check
  ): void {
    this.http
      .updateCheckFromCompany$(companyID, employeeID, checkID, check)
      .subscribe({
        next: this.nextUpdateCheckFromCompany,
        error: this.errorUpdateCheckFromCompany,
      });
  }

  private nextUpdateCheckFromCompany = (
    data: CheckRegisterApiResponse
  ): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const check: Check = data.body.result[0] as Check;
      const journey: Journey = data.body.included as unknown as Journey;
      this.checkSubject.next(check);
      this.journeySubject.next(journey);
      this.snackbars.successRegisterCheck();
    } else {
      this.checkSubject.next(null);
      this.journeySubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorUpdateCheckFromCompany = (error: HttpErrorResponse): void => {
    this.checkSubject.next(null);
    this.journeySubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0004')) {
      this.snackbars.failureWrongHours();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0008')) {
      this.snackbars.failureEmployeeInvalidCredentials();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0020')) {
      this.snackbars.failureEmployeeNotWithInWorkplaceAccessRadius();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0028')) {
      this.snackbars.failureCheckDateCollidesWithAnExistingOne();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0030')) {
      this.snackbars.failureCheckCalendarIsClosed();
    } else if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0055')) {
      this.snackbars.failureEmployeeOnLeave();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Delete Check
  // ==========================================================================================

  public getMessage$(): Observable<string> {
    return this.messageSubject.asObservable();
  }

  public deleteCheckFromCompany(
    companyID: number,
    employeeID: number,
    checkID: number
  ): void {
    this.http
      .deleteCheckFromCompany$(companyID, employeeID, checkID)
      .subscribe({
        next: this.nextDeleteCheckFromCompany,
        error: this.errorDeleteCheckFromCompany,
      });
  }

  private nextDeleteCheckFromCompany = (
    data: CheckRegisterApiResponse
  ): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const journey: Journey = data.body.included as unknown as Journey;
      this.messageSubject.next('OK delete check');
      this.journeySubject.next(journey);
      this.snackbars.successDeleteCheckEmployee();
    } else {
      this.messageSubject.next(null);
      this.journeySubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorDeleteCheckFromCompany = (error: HttpErrorResponse): void => {
    this.messageSubject.next(null);
    this.journeySubject.next(null);
    this.snackbars.failDelete();
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Send Checks Report From Company
  // ==========================================================================================

  public getChecksReport$(): Observable<CheckReport | null> {
    return this.checksReportSubject.asObservable().pipe(shareReplay(1));
  }

  public sendChecksReport(
    companyID: number,
    employeeID: number,
    month: Month
  ): void {
    this.http.sendChecksReport$(companyID, employeeID, month).subscribe({
      next: this.nextSendChecksReport,
      error: this.errorSendChecksReport,
    });
  }

  private nextSendChecksReport = (data: CheckReportApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const report: CheckReport = data.body.result[0];
      this.checksReportSubject.next(report);
      this.snackbars.successSentReportByEmail();
    } else {
      this.checksReportSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorSendChecksReport = (error: HttpErrorResponse): void => {
    this.checksReportSubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0004')) {
      this.snackbars.failureInvalidField();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  public getCheckHistorySubject$() {
    return this.historySubject.asObservable().pipe(shareReplay(1));
  }

  public checkHistory(companyID: number, employeeID: number, checkID: number) {
    this.http.checkHistory$(companyID, employeeID, checkID).subscribe({
      next: this.nextCheckHistory,
      error: this.errorCheckHistory,
    });
  }

  private nextCheckHistory = (data: CheckHistoryApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const history: CheckHistory[] = data.body.result;
      this.historySubject.next(history);
    } else {
      this.historySubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorCheckHistory = (error: HttpErrorResponse): void => {
    this.historySubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };
}
